/** Situação do termo, na grafia do backend. */
export type CommercialClientTermStatus =
  | 'Pendente'
  | 'Enviado'
  | 'AssinadoParcialmente'
  | 'Assinado'
  | 'Cancelado';

/** De que lado a pessoa assina. */
export type CommercialClientTermSignatorySide = 'Cliente' | 'Legatus';

/** De onde o assinante veio. */
export type CommercialClientTermSignatoryOrigin = 'Receita' | 'Manual' | 'Parametrizacao';

/**
 * Rótulos de situação.
 *
 * `Record<...>` de propósito: etapa nova no backend quebra a compilação aqui, em vez de virar
 * um rótulo vazio na tela.
 */
export const COMMERCIAL_CLIENT_TERM_STATUS_LABELS: Record<CommercialClientTermStatus, string> = {
  Pendente: 'Pendente de envio',
  Enviado: 'Aguardando assinaturas',
  AssinadoParcialmente: 'Assinado parcialmente',
  Assinado: 'Assinado',
  Cancelado: 'Cancelado',
};

/** Cor da tag de situação, na paleta do PrimeNG. */
export const COMMERCIAL_CLIENT_TERM_STATUS_SEVERITY: Record<
  CommercialClientTermStatus,
  'success' | 'info' | 'warning' | 'danger'
> = {
  Pendente: 'warning',
  Enviado: 'info',
  AssinadoParcialmente: 'info',
  Assinado: 'success',
  Cancelado: 'danger',
};

/** Rótulos de origem do assinante. */
export const COMMERCIAL_CLIENT_TERM_ORIGIN_LABELS: Record<
  CommercialClientTermSignatoryOrigin,
  string
> = {
  Receita: 'Sócio (Receita Federal)',
  Manual: 'Incluído manualmente',
  Parametrizacao: 'Parametrização',
};

/** Quem assina o termo. */
export interface CommercialClientTermSignatoryResponse {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string | null;
  side: CommercialClientTermSignatorySide;
  origin: CommercialClientTermSignatoryOrigin;
  qualification?: string | null;
  /** Quando assinou. Nulo enquanto não assinou. */
  signedAt?: string | null;
  /**
   * Se a tela pode oferecer a remoção.
   *
   * ⚠️ Calculado **no servidor**, e não aqui: quem já assinou não sai, o último assinante do
   * cliente não sai, e o lado Legatus vem da Parametrização. Recalcular no front duplicaria a
   * regra e as duas versões acabariam discordando.
   */
  canBeRemoved: boolean;
}

/**
 * Um sócio do cliente na Receita, oferecido como sugestão de assinante.
 *
 * ⚠️ **Sem e-mail e sem CPF — a Receita não os informa.** Como o provedor de assinatura exige
 * e-mail, a lista diz *quem* deve assinar, e a pessoa completa o endereço. A tela precisa deixar
 * isso claro: não existe "marcar os sócios e enviar".
 */
export interface CommercialClientTermSuggestedSignatoryResponse {
  name: string;
  qualification?: string | null;
  /** Já está na lista de assinantes — calculado no servidor, por nome normalizado. */
  alreadyAdded: boolean;
}

/**
 * O NDA + carta de autorização de SCR do cliente (card 2951).
 *
 * ⚠️ É **um termo por CLIENTE**, não por deal. `reusedFromAnotherDeal` avisa que ele nasceu antes
 * deste deal — mexer nos assinantes por aqui muda o que o outro deal mostra.
 */
export interface CommercialClientTermResponse {
  id: string;
  companyId: string;
  companyName?: string | null;
  termsVersion: string;
  status: CommercialClientTermStatus;
  requestedAt?: string | null;
  /** Quando o termo fechou — a data da **última** assinatura, não a do processamento. */
  signedAt?: string | null;
  expiresAt?: string | null;
  reusedFromAnotherDeal: boolean;
  signatories: CommercialClientTermSignatoryResponse[];
}

/**
 * Quem assina pela Legatus, vindo da Parametrização.
 *
 * ⚠️ **Só leitura, e sem CPF.** O parceiro não escolhe quem assina pelo nosso lado, e o
 * documento dessas pessoas não é dele — o backend devolve nome e e-mail apenas.
 */
export interface LegatusTermSignatoryResponse {
  name: string;
  email: string;
}

/** Método de autenticação do signatário do cliente, na grafia do Clicksign. */
export type TermSignerAuth = 'email' | 'sms' | 'whatsapp';

/**
 * Como o signatário do cliente confirma a identidade no Clicksign (card 3073), vindo da
 * Parametrização (`legatus-term-signer-verification`). Vale só para o lado do cliente.
 */
export interface TermSignerVerificationResponse {
  auth: TermSignerAuth;
  selfie: boolean;
  officialDocument: boolean;
  handwritten: boolean;
  /** SMS e WhatsApp: sem celular, o envio é recusado. */
  requiresPhone: boolean;
}

/**
 * O documento que **será** enviado, montado com os assinantes escolhidos.
 *
 * Nada é gravado para gerar isto: nem linha de termo, nem blob. O conteúdo vem em base64 para
 * que a recusa por regra de negócio chegue pelo mesmo caminho de erro dos outros endpoints.
 */
export interface CommercialClientTermPreviewResponse {
  contentBase64: string;
  fileName: string;
}

/**
 * Link temporário para ver o documento do termo.
 *
 * ⚠️ O link é **inline**: o PDF renderiza no visualizador do navegador em vez de cair na pasta
 * de downloads. É o que o botão "Ver termo" promete — baixar, quem quiser faz pelo visualizador.
 */
export interface CommercialClientTermDocumentResponse {
  url: string;
  fileName: string;
  /**
   * Se o que vai ser mostrado é a **via assinada**.
   *
   * A minuta e o documento com as assinaturas são o mesmo arquivo em dois momentos. A tela
   * distingue os dois porque arquivar a minuta achando que é a prova é um erro caro.
   */
  signed: boolean;
}
